// Single source of truth for which notification types are "ambient" vs
// "direct interactions".
//
// Direct interactions = something another user or the system did TO or ABOUT
// you (a follow, a wave, a like on your content, an approval of your
// submission, a badge you earned). These belong in the bell / Activity center,
// where the unread badge means "something about me happened."
//
// Ambient = broadcast, nudge, or nearby-activity notifications that are not a
// direct interaction (someone checked in near you, a new café opened, weekly
// recap, re-engagement nudges). Following the pattern used by Instagram, X,
// Strava, Untappd, etc., these are delivered via push + the in-app activity
// banner + the Feed, and are intentionally kept OUT of the bell so it stays a
// clean record of personal interactions.
//
// This is a denylist on purpose: any notification type not listed here is
// treated as a direct interaction and still shows in the bell. That way a newly
// added personal-interaction type is never accidentally hidden — only the known
// ambient set below is filtered out.

export const AMBIENT_NOTIFICATION_TYPES = [
  "checkin_nearby", // someone checked in near you (this feature)
  "new_cafe_nearby", // a new café was added near you
  "friends_activity_nudge", // weekly "people you follow are checking in" nudge
  "weekly_recap", // Monday summary
  "streak_reminder", // "keep your streak alive" nudge (NOT streak_milestone)
  "morning_nudge", // daily morning prompt
  "saved_cafe_nudge", // "still thinking about this saved café?"
  "reengagement_nudge", // "we miss you" win-back
] as const;

export type AmbientNotificationType = (typeof AMBIENT_NOTIFICATION_TYPES)[number];

const AMBIENT_SET = new Set<string>(AMBIENT_NOTIFICATION_TYPES);

/** True when a notification type is ambient (kept out of the bell). */
export function isAmbientNotificationType(type: string): boolean {
  return AMBIENT_SET.has(type);
}

/**
 * PostgREST list literal for a `not.in` filter on the notification type column,
 * e.g. "(checkin_nearby,new_cafe_nearby,...)". Used to exclude ambient rows from
 * both the bell list query and its unread-count query.
 */
export function ambientTypesPostgrestList(): string {
  return `(${AMBIENT_NOTIFICATION_TYPES.join(",")})`;
}
